import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPreviousClick: () => void;
  onNextClick: () => void;
  onPageNumberClick: (page: number) => void;
  rows: number;
  handleRowsChange: (e: SelectChangeEvent<number>) => void;
  itemsCount: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onNextClick,
  onPageNumberClick,
  onPreviousClick,
  rows,
  handleRowsChange,
  itemsCount,
}) => {
  const getVisiblePages = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }
    if (currentPage <= 3) {
      pages.push(1, 2, 3, "...", totalPages - 1, totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 2, "...", totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages,
      );
    }
    return pages;
  };

  const visiblePages = getVisiblePages();

  // Generate valid row options (when itemsCount is 0, default to [10] to avoid MUI out-of-range value warning)
  const validRowOptions = [
    ...Array.from(
      { length: Math.ceil(itemsCount / 10) },
      (_, i) => (i + 1) * 10,
    ).filter((value) => value <= itemsCount),
    ...(itemsCount > 0 ? [itemsCount] : []),
  ];
  const options = validRowOptions.length > 0 ? validRowOptions : [10];
  const safeValue = options.includes(rows) ? rows : options[0];

  const handleRowsChangeWithReset = (e: SelectChangeEvent<number>) => {
    handleRowsChange(e);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      pb={2}
      px={1.5}
      gap={2}
    >
      <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <Select
          id="rows-per-page-select"
          variant="outlined"
          sx={{
            height: "40px",
            minWidth: "60px",
            borderRadius: "8px",
            backgroundColor: "white",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
          }}
          value={safeValue}
          onChange={handleRowsChangeWithReset}
        >
          {options
            .filter((value) => value !== itemsCount)
            .map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}

          {itemsCount > 0 && <MenuItem value={itemsCount}>All</MenuItem>}
        </Select>
        <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
          Show rows
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "white",
          borderRadius: "8px",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        <Button
          variant="text"
          disabled={currentPage === 1}
          onClick={onPreviousClick}
          sx={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            borderRadius: 0,
            px: 2,
            py: 1,
            "&:disabled": {
              color: "#9CA3AF",
            },
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          <ArrowBackIcon sx={{ width: "18px", height: "18px" }} />
          Previous
        </Button>
        {visiblePages.map((pageNum, index) =>
          typeof pageNum === "number" ? (
            <Button
              key={pageNum}
              variant="text"
              sx={{
                borderRadius: 0,
                minWidth: "40px",
                px: 1.5,
                py: 1,
                backgroundColor:
                  pageNum === currentPage
                    ? `gray[100] !important`
                    : "transparent",
                fontWeight: pageNum === currentPage ? 600 : 400,
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                },
              }}
              onClick={() => onPageNumberClick(pageNum)}
            >
              {pageNum}
            </Button>
          ) : (
            <Typography
              key={`ellipsis-${index}`}
              sx={{
                px: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              ...
            </Typography>
          ),
        )}
        <Button
          variant="text"
          disabled={currentPage === totalPages}
          onClick={onNextClick}
          sx={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            borderRadius: 0,
            px: 2,
            py: 1,
            "&:disabled": {
              color: "#9CA3AF",
            },
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Next
          <ArrowForwardIcon sx={{ width: "18px", height: "18px" }} />
        </Button>
      </Box>
    </Box>
  );
};

export default Pagination;
